"use client"

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface UnsavedChangesDialogProps {
  open: boolean
  onStay: () => void
  onDiscard: () => void
  title?: string
  message?: string
  stayLabel?: string
  discardLabel?: string
}

/** Confirm discard when leaving a form with unsaved edits (Esc / Close / sidebar / logout). */
export function UnsavedChangesDialog({
  open,
  onStay,
  onDiscard,
  title = "Unsaved changes",
  message = "You have unsaved changes. Discard them?",
  stayLabel = "Keep editing",
  discardLabel = "Discard",
}: UnsavedChangesDialogProps) {
  // 0 = Keep editing, 1 = Discard
  const [focusIdx, setFocusIdx] = useState(0)
  const focusIdxRef = useRef(0)
  const panelRef = useRef<HTMLDivElement>(null)
  const stayRef = useRef(onStay)
  const discardRef = useRef(onDiscard)
  stayRef.current = onStay
  discardRef.current = onDiscard

  useEffect(() => {
    focusIdxRef.current = focusIdx
  }, [focusIdx])

  useEffect(() => {
    if (!open) return
    setFocusIdx(0)
    focusIdxRef.current = 0
    const id = requestAnimationFrame(() => panelRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        stayRef.current()
        return
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault()
        e.stopPropagation()
        setFocusIdx(0)
        return
      }
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault()
        e.stopPropagation()
        setFocusIdx(1)
        return
      }
      if (e.key === "Tab") {
        e.preventDefault()
        e.stopPropagation()
        setFocusIdx((i) => (i === 0 ? 1 : 0))
        return
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        e.stopPropagation()
        if (focusIdxRef.current === 0) stayRef.current()
        else discardRef.current()
      }
    }
    document.addEventListener("keydown", onKeyDown, true)
    return () => document.removeEventListener("keydown", onKeyDown, true)
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      role="presentation"
      onClick={onStay}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full max-w-sm rounded-xl bg-background p-5 shadow-lg ring-1 ring-foreground/10 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
        <p className="mt-1 text-[11px] text-muted-foreground/80">
          ← → to choose · Enter to confirm · Esc to keep editing
        </p>
        <div className="mt-5 flex justify-end gap-2" role="group" aria-label="Actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-pressed={focusIdx === 0}
            className={cn(focusIdx === 0 && "ring-2 ring-ring ring-offset-2")}
            onMouseEnter={() => setFocusIdx(0)}
            onClick={onStay}
          >
            {stayLabel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            aria-pressed={focusIdx === 1}
            className={cn(focusIdx === 1 && "ring-2 ring-ring ring-offset-2")}
            onMouseEnter={() => setFocusIdx(1)}
            onClick={onDiscard}
          >
            {discardLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
